import React, { Component, Fragment } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { injectIntl } from "react-intl";
import {
  Grid,
  IconButton,
  Chip,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
} from "@material-ui/core";
import { Search as SearchIcon, People as PeopleIcon, Tab as TabIcon, Delete as DeleteIcon } from "@material-ui/icons";
import {
  withModulesManager,
  formatMessageWithValues,
  formatDateFromISO,
  formatMessage,
  withHistory,
  historyPush,
  coreConfirm,
  journalize,
  Searcher,
  PublishedComponent,
  downloadExport,
} from "@openimis/fe-core";
import EnquiryDialog from "./EnquiryDialog";
import { RIGHT_INSUREE_DELETE, INSUREE_MARITAL_STATUS, DEFAULT } from "../constants";
import { fetchInsureeSummaries, deleteInsuree, downloadWorkers, clearWorkersExport } from "../actions";

import InsureeFilter from "./InsureeFilter";
import { insureeLabel } from "../utils/utils";

const INSUREE_SEARCHER_CONTRIBUTION_KEY = "insuree.InsureeSearcher";

class InsureeSearcher extends Component {
  state = {
    open: false,
    chfid: null,
    confirmedAction: null,
    reset: 0,
    failedExport: false,
    showSecondaryColumns: false,
  };

  constructor(props) {
    super(props);
    this.rowsPerPageOptions = props.modulesManager.getConf(
      "fe-insuree",
      "insureeFilter.rowsPerPageOptions",
      [10, 20, 50, 100],
    );
    this.defaultPageSize = props.modulesManager.getConf("fe-insuree", "insureeFilter.defaultPageSize", 10);
    this.locationLevels = this.props.modulesManager.getConf("fe-location", "location.Location.MaxLevels", 4);
    this.isWorker = props.modulesManager.getConf("fe-core", "isWorker", DEFAULT.IS_WORKER);
    this.renderLastNameFirst = props.modulesManager.getConf(
      "fe-insuree",
      "renderLastNameFirst",
      DEFAULT.RENDER_LAST_NAME_FIRST,
    );
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (!prevProps.errorWorkersExport && this.props.errorWorkersExport) {
      this.setState({ failedExport: true });
    }

    if (prevProps.workersExport !== this.props.workersExport && this.props.workersExport) {
      const { clearWorkersExport, workersExport, intl } = this.props;
      downloadExport(workersExport, `${formatMessage(intl, "insuree", "workers.filename")}.csv`)();
      clearWorkersExport();
    }

    if (prevProps.submittingMutation && !this.props.submittingMutation) {
      this.props.journalize(this.props.mutation);
      this.setState({ reset: this.state.reset + 1 });
    } else if (!prevProps.confirmed && this.props.confirmed && !!this.state.confirmedAction) {
      this.state.confirmedAction();
    }
  }

  fetch = (prms) => {
    this.props.fetchInsureeSummaries(this.props.modulesManager, prms, this.isWorker);
  };

  rowIdentifier = (r) => r.uuid;

  filtersToQueryParams = (state) => {
    let prms = Object.keys(state.filters)
      .filter((f) => !!state.filters[f]["filter"])
      .map((f) => state.filters[f]["filter"]);
    if (!state.beforeCursor && !state.afterCursor) {
      prms.push(`first: ${state.pageSize}`);
    }
    if (!!state.afterCursor) {
      prms.push(`after: "${state.afterCursor}"`);
      prms.push(`first: ${state.pageSize}`);
    }
    if (!!state.beforeCursor) {
      prms.push(`before: "${state.beforeCursor}"`);
      prms.push(`last: ${state.pageSize}`);
    }
    if (!!state.orderBy) {
      prms.push(`orderBy: ["${state.orderBy}"]`);
    }
    return prms;
  };

  _toggleSecondaryColumns = (event) => {
    this.setState({ showSecondaryColumns: event.target.checked });
  };

  _tableTitle = (count) => {
    const { intl } = this.props;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <span>{formatMessageWithValues(intl, "insuree", "insureeSummaries", { count })}</span>
        <FormControlLabel
          control={
            <Checkbox
              color="primary"
              checked={this.state.showSecondaryColumns}
              onChange={this._toggleSecondaryColumns}
            />
          }
          label={
            <span style={{ fontSize: 12, fontWeight: 600 }}>
              {formatMessage(intl, "insuree", "insureeSummaries.showSecondaryColumns")}
            </span>
          }
        />
      </div>
    );
  };

  headers = (filters) => {
    if (this.isWorker) {
      return [
        "insuree.insureeSummaries.shortInsuranceNo",
        this.renderLastNameFirst ? "insuree.insureeSummaries.shortLastName" : "insuree.insureeSummaries.shortOtherNames",
        !this.renderLastNameFirst ? "insuree.insureeSummaries.shortLastName" : "insuree.insureeSummaries.shortOtherNames",
        "",
      ];
    }
    const h = [
      "insuree.insureeSummaries.shortInsuranceNo",
      this.renderLastNameFirst ? "insuree.insureeSummaries.shortLastName" : "insuree.insureeSummaries.shortOtherNames",
      !this.renderLastNameFirst ? "insuree.insureeSummaries.shortLastName" : "insuree.insureeSummaries.shortOtherNames",
      "insuree.insureeSummaries.gender",
      "insuree.insureeSummaries.dob",
      "insuree.insureeSummaries.phone",
      "insuree.insureeSummaries.family",
      "insuree.insureeSummaries.status",
    ];
    if (this.state.showSecondaryColumns) {
      h.push(
        "insuree.insureeSummaries.email",
        "insuree.insureeSummaries.maritalStatus",
        ...Array.from(Array(this.locationLevels)).map((_, i) => `location.locationType.${i}`),
      );
      if (filters?.showHistory?.value) {
        h.push("insuree.insureeSummaries.validityFrom", "insuree.insureeSummaries.validityTo");
      }
    }
    h.push("");
    return h;
  };

  sorts = (filters) => {
    if (this.isWorker) {
      return [["chfId", true], this.renderLastNameFirst ? ["lastName", true] : ["otherNames", true], !this.renderLastNameFirst ? ["lastName", true] : ["otherNames", true], null];
    }
    const results = [
      ["chfId", true],
      this.renderLastNameFirst ? ["lastName", true] : ["otherNames", true],
      !this.renderLastNameFirst ? ["lastName", true] : ["otherNames", true],
      ["gender__code", true],
      ["dob", true],
      ["phone", true],
      null,
      null,
    ];
    if (this.state.showSecondaryColumns) {
      results.push(["email", true], ["marital", true]);
      _.times(this.locationLevels, () => results.push(null));
      if (filters?.showHistory?.value) {
        results.push(["validityFrom", false], ["validityTo", false]);
      }
    }
    results.push(null);
    return results;
  };

  rowSecondaryHighlighted = (insuree) => !insuree?.family;

  parentLocation = (location, level) => {
    if (!location) return "";
    let loc = location;
    for (var i = 1; i < this.locationLevels - level; i++) {
      if (!loc.parent) return "";
      loc = loc.parent;
    }
    return !!loc ? loc.name : "";
  };

  handleClose = () => {
    this.setState({ open: false, chfid: null });
  };

  handleExportErrorDialogClose = () => {
    this.setState({ failedExport: false });
  };

  confirmDelete = (i) => {
    let confirmedAction = () =>
      this.props.deleteInsuree(
        this.props.modulesManager,
        !!i.family ? i.family.uuid : null,
        i,
        formatMessageWithValues(this.props.intl, "insuree", "DeleteInsuree.mutationLabel", { label: insureeLabel(i) }),
      );
    let confirm = (e) =>
      this.props.coreConfirm(
        formatMessageWithValues(this.props.intl, "insuree", "deleteInsureeDialog.title", { label: insureeLabel(i) }),
        formatMessageWithValues(this.props.intl, "insuree", "deleteInsureeDialog.message", {
          label: insureeLabel(i),
        }),
      );
    this.setState({ confirmedAction }, confirm);
  };

  itemFormatters = (filters) => {
    const formatters = [
      (insuree) => insuree.chfId,
      (insuree) => <Typography noWrap>{(this.renderLastNameFirst ? insuree.lastName : insuree.otherNames) || ""}</Typography>,
      (insuree) => <Typography noWrap>{(!this.renderLastNameFirst ? insuree.lastName : insuree.otherNames) || ""}</Typography>,
    ];
    if (!this.isWorker) {
      formatters.push(
        (insuree) => (
          <Chip
            size="small"
            label={formatMessage(this.props.intl, "insuree", `gender.${insuree.gender?.code}`)}
            style={{ backgroundColor: "#e3f2fd", color: "#004A7C", fontWeight: 600 }}
          />
        ),
        (insuree) => formatDateFromISO(this.props.modulesManager, this.props.intl, insuree.dob),
        (insuree) => insuree.phone,
        (insuree) => <Typography noWrap>{insuree.family?.headInsuree?.chfId || ""}</Typography>,
        (insuree) => {
          const statusColors = {
            AC: { backgroundColor: "#dff3e6", color: "#176b3a" },
            IN: { backgroundColor: "#fff4d6", color: "#8a5a00" },
            DE: { backgroundColor: "#fde4e1", color: "#a02a1f" },
          };
          return (
            <Chip
              size="small"
              label={formatMessage(this.props.intl, "insuree", `Insuree.status.${insuree.status}`)}
              style={{ fontWeight: 600, ...(statusColors[insuree.status] || {}) }}
            />
          );
        },
      );
      if (this.state.showSecondaryColumns) {
        formatters.push(
          (insuree) => insuree.email,
          (insuree) => (
            <PublishedComponent
              pubRef="insuree.InsureeMaritalStatusPicker"
              withLabel={false}
              readOnly={true}
              value={insuree.marital || INSUREE_MARITAL_STATUS[0]}
            />
          ),
        );
        for (let i = 0; i < this.locationLevels; i++) {
          let j = i + 0;
          formatters.push((insuree) =>
            this.parentLocation(insuree.currentVillage || (!!insuree.family && insuree.family.location), j),
          );
        }
        if (filters?.showHistory?.value) {
          formatters.push(
            (insuree) => formatDateFromISO(this.props.modulesManager, this.props.intl, insuree.validityFrom),
            (insuree) => formatDateFromISO(this.props.modulesManager, this.props.intl, insuree.validityTo),
          );
        }
      }
    }
    formatters.push(
      (insuree) => (
        <Grid container wrap="nowrap" spacing="2">
          {!this.isWorker && (
            <Grid item>
              <IconButton
                size="small"
                onClick={(e) => !insuree.clientMutationId && this.setState({ open: true, chfid: insuree.chfId })}
              >
                <SearchIcon />
              </IconButton>
            </Grid>
          )}
          {!this.isWorker && insuree.family && (
            <Grid item>
              <Tooltip title={formatMessage(this.props.intl, "insuree", "insureeSummaries.openFamilyButton.tooltip")}>
                <IconButton
                  size="small"
                  onClick={(e) =>
                    !insuree.clientMutationId &&
                    historyPush(this.props.modulesManager, this.props.history, "insuree.route.familyOverview", [
                      insuree.family.uuid,
                    ])
                  }
                >
                  <PeopleIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
          <Grid item>
            <Tooltip title={formatMessage(this.props.intl, "insuree", "insureeSummaries.openNewTabButton.tooltip")}>
              <IconButton
                size="small"
                onClick={(e) => !insuree.clientMutationId && this.props.onDoubleClick(insuree, true)}
              >
                <TabIcon />
              </IconButton>
            </Tooltip>
          </Grid>
          {this.props.rights.includes(RIGHT_INSUREE_DELETE) && !insuree.validityTo && (
            <Grid item>
              <Tooltip title={formatMessage(this.props.intl, "insuree", "insureeSummaries.deleteFamily.tooltip")}>
                <IconButton size="small" onClick={(e) => !insuree.clientMutationId && this.confirmDelete(insuree)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        </Grid>
      ),
    );
    return formatters.filter(Boolean);
  };

  rowDisabled = (selection, i) => !!i.validityTo;
  rowLocked = (selection, i) => !!i.clientMutationId;

  render() {
    const {
      intl,
      insurees,
      insureesPageInfo,
      fetchingInsurees,
      fetchedInsurees,
      errorInsurees,
      filterPaneContributionsKey,
      cacheFiltersKey,
      onDoubleClick,
      errorWorkersExport,
      downloadWorkers,
    } = this.props;
    const { failedExport } = this.state;

    let count = (insureesPageInfo?.totalCount || 0).toLocaleString();

    return (
      <Fragment>
        <EnquiryDialog open={this.state.open} chfid={this.state.chfid} onClose={this.handleClose} />
        <Searcher
          module="insuree"
          cacheFiltersKey={cacheFiltersKey}
          FilterPane={InsureeFilter}
          filterPaneContributionsKey={filterPaneContributionsKey}
          items={insurees}
          itemsPageInfo={insureesPageInfo}
          fetchingItems={fetchingInsurees}
          fetchedItems={fetchedInsurees}
          errorItems={errorInsurees}
          contributionKey={INSUREE_SEARCHER_CONTRIBUTION_KEY}
          tableTitle={this._tableTitle(count)}
          rowsPerPageOptions={this.rowsPerPageOptions}
          defaultPageSize={this.defaultPageSize}
          fetch={this.fetch}
          rowIdentifier={this.rowIdentifier}
          rowSecondaryHighlighted={this.rowSecondaryHighlighted}
          filtersToQueryParams={this.filtersToQueryParams}
          defaultOrderBy="chfId"
          headers={this.headers}
          itemFormatters={this.itemFormatters}
          sorts={this.sorts}
          rowDisabled={this.rowDisabled}
          rowLocked={this.rowLocked}
          onDoubleClick={(i) => !i.clientMutationId && onDoubleClick(i)}
          reset={this.state.reset}
          exportable={this.isWorker}
          exportFetch={downloadWorkers}
          exportFields={["chf_id", "last_name", "other_names"]}
          exportFieldsColumns={{
            chf_id: formatMessage(intl, "insuree", "Insuree.chfId"),
            last_name: formatMessage(intl, "insuree", "Insuree.lastName"),
            other_names: formatMessage(intl, "insuree", "Insuree.otherNames"),
          }}
          exportFieldLabel={formatMessage(intl, "insuree", "workers.export")}
          chooseExportableColumns
        />
        {failedExport && (
          <Dialog open={failedExport} fullWidth maxWidth="sm">
            <DialogTitle> {errorWorkersExport?.message} </DialogTitle>
            <DialogContent>
              <strong> {`${errorWorkersExport?.code}:`} </strong> {errorWorkersExport?.detail}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => this.handleExportErrorDialogClose()} color="primary" variant="contained">
                {formatMessage(intl, null, "close")}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  rights: !!state.core && !!state.core.user && !!state.core.user.i_user ? state.core.user.i_user.rights : [],
  insurees: state.insuree.insurees,
  insureesPageInfo: state.insuree.insureesPageInfo,
  fetchingInsurees: state.insuree.fetchingInsurees,
  fetchedInsurees: state.insuree.fetchedInsurees,
  errorInsurees: state.insuree.errorInsurees,
  submittingMutation: state.insuree.submittingMutation,
  mutation: state.insuree.mutation,
  confirmed: state.core.confirmed,
  workersExport: state.insuree.workersExport,
  errorWorkersExport: state.insuree.errorWorkersExport,
});

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      fetchInsureeSummaries,
      deleteInsuree,
      journalize,
      coreConfirm,
      clearWorkersExport,
      downloadWorkers,
    },
    dispatch,
  );
};

export default withModulesManager(
  withHistory(connect(mapStateToProps, mapDispatchToProps)(injectIntl(InsureeSearcher))),
);
